import { useContext } from 'react';
import { TenantContext } from '../../../context/tenant-context';
import { handleRequest } from '../quipli-service-client';
import {
  CustomProductPricingConfig,
  CustomProductPricingConfigRequest,
  ListProductPriceRequest,
  ProductPrice,
} from './pricing';
import { PricingParsers } from './pricing-parsers';
import { OrderType } from '../order/order';

export const usePricingApiV1 = () => {
  const { tenant, contextPath } = useContext(TenantContext);

  async function listPrices(request: ListProductPriceRequest): Promise<ProductPrice[]> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/v1/product/prices`,
      headers: {
        'X-Tenant': tenant,
      },
      params: {
        ...request,
        productId: request.productIds,
        type: request.type ?? OrderType.Rental,
        sortBy: 'price',
      },
    };
    const result = await handleRequest<PricingParsers.ProductPriceJson[]>(config);
    return result.map((r) => PricingParsers.toProductPrice(r));
  }

  async function listPricesByUserId(useId: number): Promise<ProductPrice[]> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/v1/product/prices/by-user/${useId}`,
      headers: {
        'X-Tenant': tenant,
      },
    };
    const result = await handleRequest<PricingParsers.ProductPriceJson[]>(config);
    return result.map((r) => PricingParsers.toProductPrice(r));
  }

  async function listCustomProductPricingConfigs(): Promise<CustomProductPricingConfig[]> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/v1/pricing/config/products/custom`,
      headers: {
        'X-Tenant': tenant,
      },
    };
    const result = await handleRequest<PricingParsers.CustomProductPricingConfigJson[]>(config);
    return result.map((r) => PricingParsers.toCustomProductPricingConfig(r));
  }

  async function createCustomProductPricingConfig(
    request: CustomProductPricingConfigRequest,
  ): Promise<CustomProductPricingConfig> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/v1/pricing/config/products/custom`,
      headers: {
        'X-Tenant': tenant,
      },
      data: request,
    };
    const result = await handleRequest<PricingParsers.CustomProductPricingConfigJson>(config);
    return PricingParsers.toCustomProductPricingConfig(result);
  }

  async function updateCustomProductPricingConfig(
    id: string,
    request: CustomProductPricingConfigRequest,
  ): Promise<CustomProductPricingConfig> {
    const config = {
      method: 'put',
      url: `${contextPath}quipli-service/v1/pricing/config/products/custom/${id}`,
      headers: {
        'X-Tenant': tenant,
      },
      data: request,
    };
    const result = await handleRequest<PricingParsers.CustomProductPricingConfigJson>(config);
    return PricingParsers.toCustomProductPricingConfig(result);
  }

  async function deleteCustomProductPricingConfig(id: string): Promise<number> {
    const config = {
      method: 'delete',
      url: `${contextPath}quipli-service/v1/pricing/config/products/custom/${id}`,
      headers: {
        'X-Tenant': tenant,
      },
    };
    return await handleRequest<number>(config);
  }

  return {
    listPrices,
    listPricesByUserId,
    listCustomProductPricingConfigs,
    createCustomProductPricingConfig,
    updateCustomProductPricingConfig,
    deleteCustomProductPricingConfig,
  };
};
